import { defineStore } from 'pinia'
import { ref } from 'vue'
import { createKoraClient } from '@architeia/api-client'
import { useAuthStore } from '@/stores/auth'

/**
 * KORA-154: the 18+ confirmation asked at first sign-in, so no account for a minor is used in the
 * pilot. Kora keeps only the time of the confirmation (KORA-146), never a date of birth.
 *
 * `missing` sends the member to the confirmation page; `unknown` (Kora could not be asked) lets
 * them through: sharing stays refused server-side until they confirm, so failing open here costs
 * nothing and an outage does not lock everyone out.
 */
export const useAgeGateStore = defineStore('ageGate', () => {
  const status = ref<'unknown' | 'attested' | 'missing'>('unknown')
  const confirming = ref(false)
  const error = ref(false)

  function client() {
    return createKoraClient({ getAccessToken: useAuthStore().getAccessToken })
  }

  /** Asks Kora once; a known answer is not asked again until `reset`. */
  async function check(): Promise<'unknown' | 'attested' | 'missing'> {
    if (status.value !== 'unknown') return status.value
    try {
      status.value = (await client().getMyAgeAttestation()).attested ? 'attested' : 'missing'
    } catch {
      status.value = 'unknown'
    }
    return status.value
  }

  async function confirm(): Promise<boolean> {
    if (confirming.value) return false
    confirming.value = true
    error.value = false
    try {
      const result = await client().attestMyAge()
      status.value = result.attested ? 'attested' : 'missing'
      return result.attested
    } catch {
      error.value = true
      return false
    } finally {
      confirming.value = false
    }
  }

  function reset(): void {
    status.value = 'unknown'
    error.value = false
  }

  return { status, confirming, error, check, confirm, reset }
})
